import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { X, MapPin, Phone, Mail, Building, Loader2, ChevronDown } from 'lucide-react'
import BranchService from '../../services/branchService'
import AppURLs from '../../config/urls' // Import AppURLs for debugging

const EditBranchModal = ({ isOpen, onClose, onBranchUpdated, branch }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    address: '',
    phoneNumber: '',
    email: '',
    isActive: true
  })

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)

  // Populate form when branch prop changes
  useEffect(() => {
    if (branch && isOpen) {
      console.log('📝 Populating form with branch data:', branch)
      setFormData({
        name: branch.name || '',
        type: branch.type || '',
        address: branch.address || '',
        phoneNumber: branch.phoneNumber || '',
        email: branch.email || '',
        isActive: branch.isActive !== undefined ? branch.isActive : true
      })
      setErrors({})
    }
  }, [branch, isOpen])

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target

    // For phone number, only allow digits and limit to 10
    if (name === 'phoneNumber') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10)
      setFormData(prev => ({
        ...prev,
        [name]: digitsOnly
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Handle status toggle
  const handleStatusToggle = () => {
    setFormData(prev => ({
      ...prev,
      isActive: !prev.isActive
    }))
  }

  // Validate phone number (exactly 10 digits)
  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[0-9]{10}$/
    return phoneRegex.test(phone)
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Branch name is required'
    }

    if (!formData.type) {
      newErrors.type = 'Branch type is required'
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required'
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Mobile number is required'
    } else if (!validatePhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Phone number must be exactly 10 digits'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      console.log('❌ Form validation failed:', errors)
      return
    }

    try {
      setLoading(true)

      // Enhanced debug logging
      console.log('📤 Submitting branch update:', {
        branchId: branch._id,
        formData,
        fullURL: `${AppURLs.API}/branches/${branch._id}`,
        token: localStorage.getItem('tezlaa-access-token') ? 'Present' : 'Missing'
      })

      const response = await BranchService.updateBranch(branch._id, formData)

      if (response.success) {
        console.log('✅ Branch updated successfully:', response)

        // Notify parent component with updated data
        const updatedBranch = response.data?.branch || { ...branch, ...formData }
        onBranchUpdated(updatedBranch)

        // Close modal
        onClose()
      } else {
        console.log('⚠️ Update failed with response:', response)
        // Handle API error
        if (response.errors) {
          setErrors(response.errors)
        } else {
          setErrors({ general: response.message || 'Failed to update branch' })
        }
      }
    } catch (error) {
      console.error('❌ Error updating branch:', error)

      // Enhanced error handling
      let errorMessage = 'Failed to update branch. Please try again.'

      if (error.message.includes('Not found')) {
        errorMessage = 'Branch not found. It may have been deleted by another user.'
      } else if (error.message.includes('Unauthorized')) {
        errorMessage = 'You are not authorized to update this branch. Please login again.'
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      } else if (error.message) {
        errorMessage = error.message
      }

      setErrors({ general: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      console.log('🔒 Closing edit modal')
      setErrors({})
      onClose()
    }
  }

  if (!isOpen || !branch) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <h2 className="text-xl font-semibold text-gray-900">Edit Branch</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* General Error */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{errors.general}</p>
            </div>
          )}

          {/* Branch Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Branch name *
            </label>
            <Input
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="eg.Tezlaa-Branch1001"
              leftIcon={Building}
              error={errors.name}
              disabled={loading}
            />
          </div>

          {/* Branch Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Branch Type *
            </label>
            <div className="relative">
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                disabled={loading}
                className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-200 appearance-none ${errors.type
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                    : 'border-gray-200'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <option value="">Select branch type</option>
                <option value="warehouse">Warehouse</option>
                <option value="outlet">Outlet</option>
              </select>
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
            {errors.type && (
              <p className="text-xs text-red-600 mt-1">{errors.type}</p>
            )}
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address *
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="eg.No.31/2, Colombo 5"
              disabled={loading}
              rows={3}
              className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-200 resize-none ${errors.address
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                  : 'border-gray-200'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            />
            {errors.address && (
              <p className="text-xs text-red-600 mt-1">{errors.address}</p>
            )}
          </div>

          {/* Mobile Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile Number *
            </label>
            <Input
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              placeholder="0714457845"
              leftIcon={Phone}
              error={errors.phoneNumber}
              disabled={loading}
              maxLength={10}
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter 10 digit phone number ({formData.phoneNumber.length}/10)
            </p>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <Input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="eg.info@tezlaa.com"
              leftIcon={Mail}
              error={errors.email}
              disabled={loading}
            />
          </div>

          {/* Status Toggle */}
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <button
              type="button"
              onClick={handleStatusToggle}
              disabled={loading}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${formData.isActive ? 'bg-orange-500' : 'bg-gray-200'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.isActive ? 'translate-x-5' : 'translate-x-0'
                  }`}
              />
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-black hover:bg-gray-800 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditBranchModal